import React,{useState,useEffect,useMemo} from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './shop.css'

/* ------------------------------------------------------------------
   ICONS
   ------------------------------------------------------------------ */
const IconPlus=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>)
const IconX=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>)
const IconTrash=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16"/><path d="M10 11v6M14 11v6"/><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12"/><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>)
const IconSearch=()=>(<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>)
const IconBroadcast=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="2.2"/>
    <path d="M8.5 8.5a5 5 0 0 0 0 7"/>
    <path d="M15.5 15.5a5 5 0 0 0 0-7"/>
    <path d="M5.8 5.8a9 9 0 0 0 0 12.4"/>
    <path d="M18.2 18.2a9 9 0 0 0 0-12.4"/>
  </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS FOR THE DATA GRID — see EShopProducts for the why.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{ out[name]=String(styles.getPropertyValue(name)||'').trim() })
  return out
}

function useTableStyles(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return useMemo(()=>{
    const t=tokens
    return {
      table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
      tableWrapper:{ style:{ display:'block' } },
      head:{ style:{ color:t['--ink-3'] } },
      headRow:{
        style:{
          backgroundColor:t['--panel-raise'],
          color:t['--ink-3'],
          minHeight:'42px',
          borderBottomWidth:'1px',
          borderBottomColor:t['--line'],
          borderBottomStyle:'solid'
        }
      },
      headCells:{
        style:{
          paddingLeft:'12px',
          paddingRight:'12px',
          fontFamily:t['--mono'],
          fontSize:'10px',
          fontWeight:600,
          letterSpacing:'0.08em',
          textTransform:'uppercase',
          color:t['--ink-3']
        }
      },
      rows:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink'],
          fontFamily:t['--sans'],
          fontSize:t['--step-0']||'0.875rem',
          minHeight:'52px',
          '&:not(:last-of-type)':{
            borderBottomWidth:'1px',
            borderBottomStyle:'solid',
            borderBottomColor:t['--line-soft']
          }
        },
        highlightOnHoverStyle:{
          backgroundColor:t['--accent-wash'],
          color:t['--ink'],
          borderBottomColor:t['--line-soft'],
          outlineWidth:'0px',
          outlineColor:t['--panel'],
          transitionDuration:'0.15s',
          transitionProperty:'background-color'
        }
      },
      cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
      pagination:{
        style:{
          backgroundColor:t['--panel'],
          color:t['--ink-3'],
          fontSize:'12px',
          minHeight:'50px',
          borderTopWidth:'1px',
          borderTopStyle:'solid',
          borderTopColor:t['--line']
        },
        pageButtonsStyle:{
          color:t['--ink-2'],
          fill:t['--ink-2'],
          backgroundColor:'transparent',
          '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
          '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
          '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
        }
      },
      noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'], padding:'26px' } },
      progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
    }
  },[tokens])
}

/* Token-styled dialog. Escape closes, backdrop closes, full-bleed under 560px. */
function Sheet({open,title,eyebrow,subtitle,size,onClose,children}){
  useEffect(()=>{
    if (!open) return undefined
    const onKey=(e)=>{ if (e.key==='Escape') onClose() }
    document.addEventListener('keydown',onKey)
    return ()=>document.removeEventListener('keydown',onKey)
  },[open,onClose])

  if (!open) return null

  return (
    <div className="sh-back" onMouseDown={(e)=>{ if (e.target===e.currentTarget) onClose() }}>
      <div className={size?'sh-modal '+size:'sh-modal'} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sh-modal-head">
          <div className="sh-modal-headline">
            {eyebrow?<span className="sh-modal-eyebrow">{eyebrow}</span>:null}
            <h2 className="sh-modal-title">{title}</h2>
            {subtitle?<div className="sh-modal-sub">{subtitle}</div>:null}
          </div>
          <button type="button" className="sh-modal-x" onClick={onClose} aria-label="Κλείσιμο"><IconX/></button>
        </div>
        <div className="sh-modal-body">{children}</div>
      </div>
    </div>
  )
}

/* What the notification will look like on the phone that receives it. */
function PushPreview({title,text}){
  const t=String(title||'').trim()
  const x=String(text||'').trim()

  return (
    <div className="sh-preview">
      <div className="sh-preview-k">Προεπισκόπηση στη συσκευή</div>
      <div className="sh-push">
        <span className="sh-push-mark" aria-hidden="true">123</span>
        <div className="sh-push-body">
          <div className="sh-push-app">123 High · τώρα</div>
          <div className={t?'sh-push-t':'sh-push-t sh-push-empty'}>{t||'Ο τίτλος εμφανίζεται εδώ'}</div>
          <div className={x?'sh-push-x':'sh-push-x sh-push-empty'}>{x||'Το κείμενο εμφανίζεται εδώ'}</div>
        </div>
      </div>
    </div>
  )
}

const TITLE_MAX=40
const TEXT_MAX=250

const dayOf=(value)=>String(value===undefined||value===null?'':value).split('T')[0]

export default function PushNotifications() {
    const [data,setData]=useState([])
    const [isLoading,setIsLoading]=useState(true)
    const [show, setShow] = useState(false);
    const [record,setRecord]=useState({})

    /* compose -> confirm. Nothing leaves this screen until the operator has
       seen, in plain words, who is about to be woken up. */
    const [step,setStep]=useState('compose')
    const [ack,setAck]=useState(false)
    const [sending,setSending]=useState(false)

    /* inline feedback — this is what replaced alert() / window.confirm() */
    const [note,setNote]=useState(null)
    const [formNote,setFormNote]=useState(null)
    const [target,setTarget]=useState(null)
    const [q,setQ]=useState('')

    const tableStyles=useTableStyles()

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width:'56px'
        },
        {
            name: 'Title',
            selector: row => row.Title,
            cell: row => <span className="sh-cell-strong">{row.Title}</span>,
            sortable: true,
            minWidth:'180px'
        },
        {
            name: 'Κείμενο',
            selector: row =>  row.Text,
            sortable: true,
            minWidth:'260px',
            wrap:true
        },
        {
            name: 'Date',
            selector: row =>  dayOf(row.DateCreated),
            sortable: true,
            width:'140px'
        },
        {
            name: '',
            cell: row => (
                <button
                    type="button"
                    className="sh-icon-btn"
                    data-tone="crit"
                    onClick={()=>setTarget(row)}
                    aria-label={'Διαγραφή — '+String(row.Title||'')}
                >
                    <IconTrash/>
                </button>
            ),
            sortable: false,
            width:'70px',
            right:true
        }
    ]


    const handleClose = () => {
        setShow(false)
        setStep('compose')
        setAck(false)
    }

    useEffect(()=>{
        loadData()
    },[])




      const loadData=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/push',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setNote({tone:'crit',text:'Το ιστορικό αποστολών δεν φορτώθηκε. Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.'})
            console.log(err)
        })
      }



      const showModal=(id)=>{
        setFormNote(null)
        setStep('compose')
        setAck(false)
        setSending(false)
        if (id==0){
            setRecord({ModalTitle:'ΝΕΟ PUSH NOTIFICATION', id:0,Title:'',Text:''})
            setShow(true)
        }

      }

      /* Step 1 -> step 2. Native validation runs first, so an empty title or
         text never reaches the confirmation. */
      const review=(e)=>{
        e.preventDefault()
        setFormNote(null)
        setAck(false)
        setStep('confirm')
      }

      const save=()=>{
            setSending(true)
            const today = new Date();
            const time = today.toISOString().split('T')[1].substring(0, 8)
            const yyyy = today.getFullYear();
            let mm = today.getMonth() + 1; // Months start at 0!
            let dd = today.getDate();

            if (dd < 10) dd = '0' + dd;
            if (mm < 10) mm = '0' + mm;

            const dateSent = dd + '-' + mm + '-' + yyyy+' '+time
            const obj={...record,dateSent}

            axios.post(process.env.REACT_APP_API_SERVER+'admin/push',obj,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                setSending(false)
                if (res.data.err==0){
                    loadData()
                    setShow(false)
                    setStep('compose')
                    setAck(false)
                    setNote({tone:'ok',text:res.data.msg})
                }else{
                    setFormNote({tone:'crit',text:res.data.msg})
                }
            }).catch(err=>{
                setSending(false)
                setFormNote({tone:'crit',text:'Η αποστολή απέτυχε. Δεν στάλθηκε ειδοποίηση — δοκιμάστε ξανά.'})
                console.log(err)
            })

      }

      const deleteRecord=(id)=>{
            axios.delete(process.env.REACT_APP_API_SERVER+'admin/push/'+id,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                if (res.data.err==0){
                    loadData()
                    setTarget(null)
                    setNote({tone:'ok',text:res.data.msg})
                }else{
                    setTarget(null)
                    setNote({tone:'crit',text:res.data.msg})
                }
            }).catch(err=>{
                setTarget(null)
                setNote({tone:'crit',text:'Η διαγραφή απέτυχε. Δοκιμάστε ξανά.'})
                console.log(err)
            })
      }

      const rows=Array.isArray(data)?data:[]
      const needle=q.trim().toLowerCase()
      const shown=needle
        ? rows.filter(item=>(String(item.Title||'')+' '+String(item.Text||'')).toLowerCase().indexOf(needle)>=0)
        : rows

      const titleLen=String(record.Title||'').length
      const textLen=String(record.Text||'').length

    return (
        <div className="sh-page">

            {note
              ? <div className="sh-note" data-tone={note.tone} role="status">
                  <span className="sh-note-text">{note.text}</span>
                  <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setNote(null)}><IconX/></button>
                </div>
              : null}

            <Panel
              title="Push Notifications"
              subtitle={isLoading
                ? 'Φόρτωση…'
                : rows.length===0
                  ? 'Καμία αποστολή ακόμη'
                  : rows.length+(rows.length===1?' αποστολή':' αποστολές')+' στο ιστορικό'}
              actions={
                <div className="sh-tools">
                  {rows.length>0
                    ? <span className="sh-search">
                        <IconSearch/>
                        <input
                          className="sh-search-input"
                          type="search"
                          value={q}
                          placeholder="Αναζήτηση"
                          aria-label="Αναζήτηση στο ιστορικό"
                          onChange={(e)=>setQ(e.target.value)}
                        />
                      </span>
                    : null}
                  <Btn variant="accent" onClick={()=>showModal(0)}><IconPlus/><span>Νέο push</span></Btn>
                </div>
              }
            >
              <p className="callout">
                Κάθε αποστολή φτάνει <b>αμέσως σε όλες τις συσκευές</b> που έχουν την εφαρμογή
                εγκατεστημένη. Δεν υπάρχει επιλογή παραληπτών και δεν γίνεται ανάκληση — η διαγραφή
                παρακάτω σβήνει μόνο την εγγραφή από αυτό το ιστορικό.
              </p>

              {isLoading
                ? <div className="sh-sk" aria-hidden="true">
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                    <span className="skeleton"></span>
                  </div>
                : rows.length===0
                  ? <div className="sh-empty">
                      <div className="sh-empty-t">Καμία ειδοποίηση δεν έχει σταλεί</div>
                      <div className="sh-empty-s">
                        Ό,τι στέλνετε από εδώ εμφανίζεται στην οθόνη κλειδώματος κάθε χρήστη.
                        Θα δείτε την προεπισκόπηση και θα επιβεβαιώσετε πριν φύγει.
                      </div>
                      <div className="sh-empty-a">
                        <Btn variant="accent" onClick={()=>showModal(0)}><IconPlus/><span>Νέο push</span></Btn>
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="sh-empty">
                        <div className="sh-empty-t">Καμία αποστολή δεν ταιριάζει</div>
                        <div className="sh-empty-s">Καθαρίστε την αναζήτηση για να δείτε και τις {rows.length} αποστολές.</div>
                        <div className="sh-empty-a"><Btn onClick={()=>setQ('')}>Καθαρισμός</Btn></div>
                      </div>
                    : <>
                        <div className="sh-count">
                          <span className="sh-count-n num">{shown.length}</span>
                          <span>{needle?'αποτελέσματα αναζήτησης':'ειδοποιήσεις στο ιστορικό'}</span>
                        </div>
                        <div className="sh-table">
                          <DataTable
                            columns={columns}
                            data={shown}
                            customStyles={tableStyles}
                            highlightOnHover
                            persistTableHead
                            pagination
                            paginationPerPage={25}
                            paginationRowsPerPageOptions={[25,50,100,200]}
                            noDataComponent={<div className="sh-empty-s">Καμία ειδοποίηση.</div>}
                          />
                        </div>
                      </>}
            </Panel>

            {/* ---------------- compose / confirm ---------------- */}
            <Sheet
              open={show}
              size="sh-modal-lg"
              eyebrow={step==='confirm'?'Επιβεβαίωση αποστολής':'Σύνταξη ειδοποίησης'}
              title={record.ModalTitle}
              subtitle={step==='confirm'
                ? 'Διαβάστε ποιοι θα το λάβουν πριν πατήσετε αποστολή.'
                : 'Δείτε την προεπισκόπηση· η αποστολή γίνεται στο επόμενο βήμα.'}
              onClose={handleClose}
            >
              {formNote
                ? <div className="sh-note" data-tone={formNote.tone} role="alert">
                    <span className="sh-note-text">{formNote.text}</span>
                    <button type="button" className="sh-note-x" aria-label="Κλείσιμο" onClick={()=>setFormNote(null)}><IconX/></button>
                  </div>
                : null}

              {step==='compose'
                ? <form className="sh-form" onSubmit={review}>
                    <div className="sh-compose">
                      <div className="sh-stack">
                        <div className="sh-field">
                          <label className="sh-label" htmlFor="pn-title">
                            Τίτλος
                            <span className="sh-label-note">(έως {TITLE_MAX} chars)</span>
                            <span className="sh-count-chars" data-near={titleLen>TITLE_MAX-6?'1':'0'}>{titleLen}/{TITLE_MAX}</span>
                          </label>
                          <input
                            id="pn-title"
                            className="sh-input"
                            type="text"
                            maxLength={TITLE_MAX}
                            required
                            autoFocus
                            value={record.Title===undefined||record.Title===null?'':record.Title}
                            onChange={(e)=>setRecord({...record,Title:e.target.value})}
                          />
                        </div>

                        <div className="sh-field">
                          <label className="sh-label" htmlFor="pn-text">
                            Κείμενο
                            <span className="sh-label-note">(έως {TEXT_MAX} chars)</span>
                            <span className="sh-count-chars" data-near={textLen>TEXT_MAX-25?'1':'0'}>{textLen}/{TEXT_MAX}</span>
                          </label>
                          <textarea
                            id="pn-text"
                            className="sh-textarea"
                            maxLength={TEXT_MAX}
                            required
                            value={record.Text===undefined||record.Text===null?'':record.Text}
                            onChange={(e)=>setRecord({...record,Text:e.target.value})}
                          />
                          <div className="sh-hint">
                            Οι συσκευές κόβουν τα μεγάλα κείμενα — κρατήστε το ουσιώδες στην αρχή.
                          </div>
                        </div>
                      </div>

                      <PushPreview title={record.Title} text={record.Text}/>
                    </div>

                    <div className="sh-actions">
                      <span className="sh-actions-end">
                        <Btn onClick={handleClose}>Άκυρο</Btn>
                        <Btn variant="accent" type="submit">Συνέχεια</Btn>
                      </span>
                    </div>
                  </form>

                : <div className="sh-form">
                    <div className="sh-confirm">
                      <div className="sh-aud">
                        <IconBroadcast/>
                        <div>
                          <div className="sh-aud-t">
                            Θα σταλεί αμέσως σε κάθε συσκευή με εγκατεστημένη την εφαρμογή 123 High.
                          </div>
                          <div className="sh-aud-s">
                            Δεν υπάρχει επιλογή παραληπτών: το μήνυμα πάει σε όλους τους χρήστες που
                            έχουν ενεργές τις ειδοποιήσεις, σε όλες τις χώρες και όλα τα αθλήματα.
                            Μόλις φύγει, <b>δεν ανακαλείται</b>.
                          </div>
                        </div>
                      </div>

                      <div className="sh-facts">
                        <div className="sh-fact">
                          <span className="sh-fact-k">Τίτλος</span>
                          <span className="sh-fact-v">{record.Title}</span>
                        </div>
                        <div className="sh-fact">
                          <span className="sh-fact-k">Κείμενο</span>
                          <span className="sh-fact-v">{record.Text}</span>
                        </div>
                        <div className="sh-fact">
                          <span className="sh-fact-k">Χρόνος αποστολής</span>
                          <span className="sh-fact-v">Αμέσως μόλις πατήσετε αποστολή</span>
                        </div>
                      </div>

                      <label className="sh-check">
                        <input
                          type="checkbox"
                          checked={ack}
                          onChange={(e)=>setAck(e.target.checked)}
                        />
                        <span>
                          Κατάλαβα ότι η ειδοποίηση φεύγει <b>σε όλους τους χρήστες</b> και δεν ανακαλείται.
                        </span>
                      </label>
                    </div>

                    <PushPreview title={record.Title} text={record.Text}/>

                    <div className="sh-actions">
                      <Btn onClick={()=>setStep('compose')} disabled={sending}>Πίσω στη σύνταξη</Btn>
                      <span className="sh-actions-end">
                        <Btn onClick={handleClose} disabled={sending}>Άκυρο</Btn>
                        <Btn className="sh-btn-send" onClick={save} disabled={!ack||sending}>
                          {sending?'Αποστολή…':'ΑΠΟΣΤΟΛΗ ΣΕ ΟΛΟΥΣ'}
                        </Btn>
                      </span>
                    </div>
                  </div>}
            </Sheet>

            {/* ---------------- delete one history row ---------------- */}
            <Sheet
              open={!!target}
              eyebrow="Ιστορικό"
              title="Διαγραφή εγγραφής"
              onClose={()=>setTarget(null)}
            >
              <div className="sh-confirm">
                <div className="sh-confirm-t">Διαγραφή του «{target?target.Title:''}»;</div>
                <div className="sh-confirm-s">
                  Σβήνει μόνο η εγγραφή από αυτό το ιστορικό. Η ειδοποίηση έχει ήδη παραδοθεί στις
                  συσκευές και δεν αφαιρείται από εκεί. Η ενέργεια δεν αναιρείται.
                </div>
                <div className="sh-actions">
                  <Btn onClick={()=>setTarget(null)}>Άκυρο</Btn>
                  <span className="sh-actions-end">
                    <Btn className="sh-btn-crit" onClick={()=>deleteRecord(target?target.id:0)}>Οριστική διαγραφή</Btn>
                  </span>
                </div>
              </div>
            </Sheet>
        </div>
    )
}
